import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { formsApi, bullhornApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Eye, Layers, ChevronDown, ChevronRight,
  Type, FileText, Mail, Phone, Hash, Link2, User as UserIcon, AlignLeft,
  Calendar, Clock, ChevronDownSquare, CheckSquare, ListChecks, Circle,
  Star, PenLine, Upload, Heading1, Minus, SplitSquareHorizontal, Sparkles,
} from 'lucide-react';
import { FormFieldEditor } from '../components/forms/FormFieldEditor';
import { FormPreviewPane } from '../components/forms/FormPreviewPane';
import { InlineFormCanvas } from '../components/forms/InlineFormCanvas';
import { AiGenerateDialog } from '../components/forms/AiGenerateDialog';

// Field types organised the way Jotform groups them — Basic / Contact /
// Choice / Advanced / Layout. Each entry pairs a Lucide icon with the
// human-readable name shown in the left-rail library.
const FIELD_LIBRARY = [
  {
    category: 'Basic',
    items: [
      { type: 'text', label: 'Short Text', icon: Type },
      { type: 'textarea', label: 'Long Text', icon: AlignLeft },
      { type: 'number', label: 'Number', icon: Hash },
      { type: 'date', label: 'Date', icon: Calendar },
      { type: 'time', label: 'Time', icon: Clock },
    ],
  },
  {
    category: 'Contact',
    items: [
      { type: 'name', label: 'Full Name', icon: UserIcon },
      { type: 'email', label: 'Email', icon: Mail },
      { type: 'phone', label: 'Phone', icon: Phone },
      { type: 'url', label: 'Website', icon: Link2 },
    ],
  },
  {
    category: 'Choice',
    items: [
      { type: 'dropdown', label: 'Dropdown', icon: ChevronDownSquare },
      { type: 'radio', label: 'Single Choice', icon: Circle },
      { type: 'multi_select', label: 'Multi-select', icon: ListChecks },
      { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
      { type: 'rating', label: 'Star Rating', icon: Star },
    ],
  },
  {
    category: 'Advanced',
    items: [
      { type: 'signature', label: 'Signature', icon: PenLine },
      { type: 'file_upload', label: 'File Upload', icon: Upload },
    ],
  },
  {
    category: 'Layout',
    items: [
      { type: 'heading', label: 'Heading', icon: Heading1 },
      { type: 'paragraph', label: 'Paragraph', icon: FileText },
      { type: 'divider', label: 'Divider', icon: Minus },
      { type: 'page_break', label: 'Page Break', icon: SplitSquareHorizontal },
    ],
  },
];

const newFieldId = () => `f_${Math.random().toString(36).slice(2, 10)}`;

// Smart defaults so newly-added fields are immediately useful.
const defaultsForType = (type) => {
  if (type === 'heading') return { label: 'Section Heading' };
  if (type === 'paragraph') return { label: 'Helper text that explains this section to the candidate.' };
  if (type === 'divider') return { label: '' };
  if (type === 'page_break') return { label: 'Page Break' };
  if (['dropdown', 'multi_select', 'radio'].includes(type)) {
    return { options: [{ value: 'option1', label: 'Option 1' }, { value: 'option2', label: 'Option 2' }] };
  }
  if (type === 'checkbox') return { label: 'I agree' };
  if (type === 'name') return { label: 'Full Name' };
  if (type === 'rating') return { label: 'How would you rate this?' };
  return {};
};

const friendlyLabel = (type) => {
  const found = FIELD_LIBRARY.flatMap((g) => g.items).find((i) => i.type === type);
  return found?.label || type;
};

export default function FormBuilderPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { getTenantPath, tenant } = useAuth();
  const featureEnabled = !!tenant?.settings?.enable_online_forms;

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [bullhornFields, setBullhornFields] = useState([]);
  const [bhLoaded, setBhLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [tab, setTab] = useState('build');
  const [dirty, setDirty] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const autoSaveTimer = useRef(null);

  const [bullhornFileTypes, setBullhornFileTypes] = useState([]);

  const loadForm = useCallback(async () => {
    try {
      const { data } = await formsApi.get(formId);
      setForm(data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load form'));
      navigate(getTenantPath('/forms'));
    } finally {
      setLoading(false);
    }
  }, [formId, navigate, getTenantPath]);

  useEffect(() => {
    if (!featureEnabled) {
      navigate(getTenantPath('/forms'));
      return;
    }
    loadForm();
  }, [featureEnabled, loadForm, navigate, getTenantPath]);

  // Lazy-load Bullhorn entity fields for the mapping dropdown.
  useEffect(() => {
    if (!form?.entity_type || bhLoaded) return;
    bullhornApi
      .getEntityFields(form.entity_type)
      .then((res) => {
        setBullhornFields(res.data?.fields || res.data || []);
        setBhLoaded(true);
      })
      .catch(() => setBhLoaded(true));
  }, [form?.entity_type, bhLoaded]);

  // Lazy-load Bullhorn file types once for the Settings tab dropdown.
  useEffect(() => {
    if (!form?.entity_type) return;
    bullhornApi
      .getFileTypes(form.entity_type)
      .then((res) => setBullhornFileTypes(res.data?.file_types || res.data?.types || res.data || []))
      .catch(() => setBullhornFileTypes([]));
  }, [form?.entity_type]);

  // Mark the form dirty whenever it changes after the initial load. The
  // autosave effect below clears the flag after a successful save.
  const updateForm = useCallback((patch) => {
    setForm((prev) => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      return next;
    });
    setDirty(true);
  }, []);

  // Debounced autosave — fires 1.5s after the last edit. Mirrors the
  // expectation set by Jotform / Google Forms where the user never has
  // to think about saving manually.
  useEffect(() => {
    if (!dirty || !form) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await formsApi.update(form.id, {
          name: form.name,
          description: form.description,
          entity_type: form.entity_type,
          status: form.status,
          fields: form.fields,
          submit_button_label: form.submit_button_label,
          completion_message: form.completion_message,
          cover_image_url: form.cover_image_url,
          accent_color: form.accent_color,
          bullhorn_file_type: form.bullhorn_file_type,
        });
        setDirty(false);
      } catch (err) {
        // Silent — surface error only on explicit save.
      } finally {
        setSaving(false);
      }
    }, 1500);
    return () => autoSaveTimer.current && clearTimeout(autoSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dirty]);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await formsApi.update(form.id, {
        name: form.name,
        description: form.description,
        entity_type: form.entity_type,
        status: form.status,
        fields: form.fields,
        submit_button_label: form.submit_button_label,
        completion_message: form.completion_message,
        cover_image_url: form.cover_image_url,
        accent_color: form.accent_color,
        bullhorn_file_type: form.bullhorn_file_type,
      });
      setDirty(false);
      toast.success('Form saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async () => {
    const next = form.status === 'published' ? 'draft' : 'published';
    try {
      await formsApi.update(form.id, { status: next });
      setForm({ ...form, status: next });
      toast.success(next === 'published' ? 'Form published' : 'Form unpublished');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Status update failed'));
    }
  };

  const addFieldAtEnd = (type) => {
    const order = (form.fields?.length || 0) + 1;
    const newField = {
      id: newFieldId(),
      field_type: type,
      label: `New ${friendlyLabel(type)}`,
      required: false,
      order,
      options: [],
      ...defaultsForType(type),
    };
    updateForm((prev) => ({ ...prev, fields: [...(prev.fields || []), newField] }));
    if (!['heading', 'paragraph', 'divider', 'page_break'].includes(type)) {
      setActiveFieldId(newField.id);
    }
  };

  const addFieldAt = (idx) => {
    // Default to a short text field when the recruiter uses the "+"
    // insert button between fields. They can switch the type from the
    // properties panel.
    const newField = {
      id: newFieldId(),
      field_type: 'text',
      label: 'New Short Text',
      required: false,
      order: idx + 1,
      options: [],
    };
    updateForm((prev) => {
      const arr = [...(prev.fields || [])];
      arr.splice(idx, 0, newField);
      return { ...prev, fields: arr.map((f, i) => ({ ...f, order: i + 1 })) };
    });
    setActiveFieldId(newField.id);
  };

  const updateField = (id, patch) => {
    updateForm((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }));
  };

  const removeField = (id) => {
    updateForm((prev) => ({ ...prev, fields: prev.fields.filter((f) => f.id !== id) }));
    if (activeFieldId === id) setActiveFieldId(null);
  };

  const duplicateField = (id) => {
    const src = form.fields.find((f) => f.id === id);
    if (!src) return;
    const copy = { ...src, id: newFieldId(), label: `${src.label} (copy)` };
    const idx = form.fields.findIndex((f) => f.id === id);
    updateForm((prev) => {
      const arr = [...prev.fields];
      arr.splice(idx + 1, 0, copy);
      return { ...prev, fields: arr.map((f, i) => ({ ...f, order: i + 1 })) };
    });
    setActiveFieldId(copy.id);
  };

  const reorderFields = (fromIdx, toIdx) => {
    updateForm((prev) => {
      const arr = [...(prev.fields || [])];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return { ...prev, fields: arr.map((f, i) => ({ ...f, order: i + 1 })) };
    });
  };

  if (loading || !form) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-muted-foreground">Loading form…</div>
      </DashboardLayout>
    );
  }

  const sortedFields = (form.fields || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const activeField = sortedFields.find((f) => f.id === activeFieldId);

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-0px)] bg-slate-50" data-testid="form-builder-page">
        {/* Top Bar */}
        <div className="border-b border-border bg-white px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(getTenantPath('/forms'))} className="gap-1.5" data-testid="form-builder-back-btn">
            <ArrowLeft className="w-4 h-4" /> Forms
          </Button>
          <div className="h-6 w-px bg-border" />
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateForm({ name: e.target.value })}
            className="font-heading text-lg text-foreground border-none focus:outline-none focus:ring-0 bg-transparent flex-1 min-w-0"
            data-testid="form-builder-title-input"
          />
          {/* Inline tab switcher */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="form-builder-tabs">
              <TabsTrigger value="build" data-testid="form-builder-tab-build">Build</TabsTrigger>
              <TabsTrigger value="settings" data-testid="form-builder-tab-settings">Settings</TabsTrigger>
            </TabsList>
          </Tabs>
          {/* Save status pill */}
          <div className="text-xs text-muted-foreground min-w-[80px] text-right">
            {saving ? 'Saving…' : dirty ? 'Unsaved' : 'Saved'}
          </div>
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${
            form.status === 'published'
              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : 'bg-slate-100 text-slate-600 border-slate-200'
          }`}>
            {form.status}
          </span>
          <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} className="gap-1.5" data-testid="form-builder-preview-btn">
            <Eye className="w-3.5 h-3.5" /> Preview
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAiOpen(true)}
            className="gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
            data-testid="form-builder-ai-generate-btn"
          >
            <Sparkles className="w-3.5 h-3.5" /> AI Generate
          </Button>
          <Button variant="outline" size="sm" onClick={handlePublishToggle} data-testid="form-builder-publish-btn">
            {form.status === 'published' ? 'Unpublish' : 'Publish'}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="form-builder-save-btn">
            <Save className="w-3.5 h-3.5" /> Save
          </Button>
        </div>

        {tab === 'build' ? (
          <div className="flex-1 grid grid-cols-12 overflow-hidden">
            {/* LEFT: categorized field library */}
            <aside className="col-span-2 border-r border-border bg-white overflow-y-auto" data-testid="form-builder-library">
              <FieldLibrary onAdd={addFieldAtEnd} />
            </aside>

            {/* MIDDLE: Inline live canvas */}
            <main className="col-span-7 overflow-y-auto p-8 pl-12">
              {sortedFields.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center max-w-3xl mx-auto" data-testid="form-builder-empty-fields">
                  <Layers className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Click a field on the left, or use the <strong>+</strong> button between fields to add your first one.
                  </p>
                </div>
              ) : (
                <InlineFormCanvas
                  fields={sortedFields}
                  activeFieldId={activeFieldId}
                  onSelect={setActiveFieldId}
                  onDuplicate={duplicateField}
                  onRemove={removeField}
                  onReorder={reorderFields}
                  onAddAt={addFieldAt}
                  onUpdate={updateField}
                />
              )}
            </main>

            {/* RIGHT: Field properties */}
            <aside className="col-span-3 border-l border-border bg-white overflow-y-auto">
              {activeField ? (
                <FormFieldEditor
                  field={activeField}
                  allFields={sortedFields}
                  bullhornFields={bullhornFields}
                  onChange={(patch) => updateField(activeField.id, patch)}
                  onClose={() => setActiveFieldId(null)}
                />
              ) : (
                <div className="p-6 text-center text-sm text-muted-foreground" data-testid="form-builder-no-field-selected">
                  <Layers className="w-8 h-8 mx-auto text-muted-foreground/60 mb-2" />
                  Select a field on the canvas to edit its properties
                </div>
              )}
            </aside>
          </div>
        ) : (
          <SettingsTab form={form} onChange={updateForm} bullhornFileTypes={bullhornFileTypes} />
        )}
      </div>

      {/* Preview dialog */}
      {showPreview && (
        <FormPreviewPane
          form={form}
          open={showPreview}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* AI generate dialog */}
      {aiOpen && (
        <AiGenerateDialog
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          entityType={form.entity_type}
          existingFieldCount={(form.fields || []).length}
          onInsert={(generatedFields) => {
            const base = (form.fields || []).length;
            const newFields = generatedFields.map((f, i) => ({ ...f, order: base + i + 1 }));
            updateForm((prev) => ({
              ...prev,
              fields: [...(prev.fields || []), ...newFields],
            }));
            setAiOpen(false);
            toast.success(`Added ${newFields.length} field${newFields.length === 1 ? '' : 's'}`);
          }}
        />
      )}
    </DashboardLayout>
  );
}

/**
 * Collapsible categorized field library — mirrors Jotform's left rail.
 */
function FieldLibrary({ onAdd }) {
  const [open, setOpen] = useState(() => {
    // Default to Basic + Contact expanded
    const init = {};
    FIELD_LIBRARY.forEach((g) => { init[g.category] = g.category === 'Basic' || g.category === 'Contact'; });
    return init;
  });

  return (
    <div className="p-3 space-y-1">
      {FIELD_LIBRARY.map((group) => (
        <div key={group.category}>
          <button
            type="button"
            onClick={() => setOpen({ ...open, [group.category]: !open[group.category] })}
            className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            data-testid={`form-library-group-${group.category.toLowerCase()}`}
          >
            <span>{group.category}</span>
            {open[group.category] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
          {open[group.category] && (
            <div className="space-y-1 mb-2">
              {group.items.map((it) => (
                <button
                  key={it.type}
                  type="button"
                  onClick={() => onAdd(it.type)}
                  className="w-full flex items-center gap-2 text-sm px-2 py-2 rounded-lg border border-transparent hover:border-primary/30 hover:bg-primary/5 transition-colors text-left"
                  data-testid={`form-builder-add-${it.type}-btn`}
                >
                  <it.icon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="flex-1 truncate">{it.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Settings tab — form-level options separate from the field editor.
 */
function SettingsTab({ form, onChange, bullhornFileTypes }) {
  return (
    <div className="flex-1 overflow-y-auto p-8" data-testid="form-builder-settings-tab">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="font-heading text-lg mb-4">General</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Form name</Label>
              <Input
                value={form.name || ''}
                onChange={(e) => onChange({ name: e.target.value })}
                data-testid="form-settings-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                rows={3}
                value={form.description || ''}
                onChange={(e) => onChange({ description: e.target.value })}
                placeholder="Shown at the top of the form"
                data-testid="form-settings-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Bullhorn entity (write-back target)</Label>
                <select
                  className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.entity_type || 'candidate'}
                  onChange={(e) => onChange({ entity_type: e.target.value })}
                  data-testid="form-settings-entity-select"
                >
                  <option value="candidate">Candidate</option>
                  <option value="contact">Contact</option>
                  <option value="placement">Placement</option>
                  <option value="company">Company</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Accent color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={form.accent_color || '#f97316'}
                    onChange={(e) => onChange({ accent_color: e.target.value })}
                    className="h-9 w-12 rounded border border-input cursor-pointer"
                    data-testid="form-settings-color-input"
                  />
                  <Input
                    value={form.accent_color || ''}
                    onChange={(e) => onChange({ accent_color: e.target.value })}
                    placeholder="(uses tenant color)"
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-white p-6" data-testid="form-settings-bullhorn-section">
          <h3 className="font-heading text-lg mb-1">Bullhorn write-back</h3>
          <p className="text-sm text-muted-foreground mb-4">
            After a candidate submits, the completed form is rendered to PDF and uploaded
            to the Bullhorn entity's <strong>Files</strong> tab. Mapped field values are
            written back to the entity record.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Bullhorn file type (category on the Files tab)</Label>
              <select
                className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={form.bullhorn_file_type || ''}
                onChange={(e) => onChange({ bullhorn_file_type: e.target.value || null })}
                data-testid="form-settings-bullhorn-file-type-select"
              >
                <option value="">Default ("Online Form")</option>
                {(bullhornFileTypes || []).map((ft) => {
                  const val = typeof ft === 'string' ? ft : (ft.value || ft.label || ft.name);
                  const lbl = typeof ft === 'string' ? ft : (ft.label || ft.name || ft.value);
                  if (!val) return null;
                  return <option key={val} value={val}>{lbl}</option>;
                })}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                This is the category candidates see when filtering the Files tab in Bullhorn.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="font-heading text-lg mb-4">Submit & thank-you</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Submit button label</Label>
              <Input
                value={form.submit_button_label || 'Submit'}
                onChange={(e) => onChange({ submit_button_label: e.target.value })}
                data-testid="form-settings-submit-label-input"
              />
            </div>
            <div>
              <Label className="text-xs">Thank-you message</Label>
              <Textarea
                rows={4}
                value={form.completion_message || ''}
                onChange={(e) => onChange({ completion_message: e.target.value })}
                placeholder="Shown to the candidate after they submit"
                data-testid="form-settings-completion-message-input"
              />
            </div>
            <div>
              <Label className="text-xs">Cover image URL (optional)</Label>
              <Input
                value={form.cover_image_url || ''}
                onChange={(e) => onChange({ cover_image_url: e.target.value })}
                placeholder="https://… (replaces the gradient banner with a hero image)"
                data-testid="form-settings-cover-image-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
